/* Phase 4 — Right-side dialer panel.
 *
 * Three states:
 *   - idle:     numeric keypad + dial bar + recent-calls list
 *   - ringing:  outgoing animation + Cancel
 *   - in_call:  timer + Recording pulse + Mute + Hangup + Quick notes
 *   - incoming: Accept/Reject UI
 *
 * Overlays the right edge from header bottom to footer top — does NOT push
 * page content. Esc collapses (when idle).
 */
import React, { useEffect, useRef, useState } from "react";
import {
    Phone, PhoneOff, PhoneIncoming, PhoneCall, MicOff, Mic,
    Delete, X, Loader2, AlertCircle, ShieldCheck, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/adminApi";
import { useDialer, sanitizeDialString, normalizePhone } from "./DialerProvider";

const KEYPAD = [
    ["1", "", "1"], ["2", "ABC", "2"], ["3", "DEF", "3"],
    ["4", "GHI", "4"], ["5", "JKL", "5"], ["6", "MNO", "6"],
    ["7", "PQRS", "7"], ["8", "TUV", "8"], ["9", "WXYZ", "9"],
    ["*", "", "*"], ["0", "+", "0"], ["#", "", "#"],
];

export default function DialerPanel() {
    const {
        open, status, error, muted, callTimer,
        identity, usedToday, dailyCap, recentCalls,
        currentLead, currentTo, setCurrentTo,
        closePanel, placeCall, hangup, toggleMute, sendDigit,
        acceptIncoming, rejectIncoming,
        lastCall, setInCallNotes, completePostCall,
    } = useDialer();

    const [notes, setNotes] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);

    // Reset notes when starting a new call + mirror to provider so post-call can prefill.
    useEffect(() => {
        if (status === "ringing" || status === "in_call") setNotes("");
    }, [status]);
    useEffect(() => {
        setInCallNotes?.(notes);
    }, [notes, setInCallNotes]);

    // Auto-save notes ~1s after the user stops typing
    useEffect(() => {
        if (status !== "in_call" || !notes) return;
        const timer = setTimeout(async () => {
            const callId = recentCalls[0]?.id;
            if (!callId) return;
            setSavingNotes(true);
            try {
                await adminApi.patchCallNotes(callId, notes);
            } catch { /* ignore */ }
            finally { setSavingNotes(false); }
        }, 1000);
        return () => clearTimeout(timer);
    }, [notes, status, recentCalls]);

    // Phase 4 — keyboard DTMF during a live call.
    // 0-9, *, # send digits via the active connection. Ignored when the user
    // is typing in any input or textarea (Quick Notes shouldn't dial tones).
    useEffect(() => {
        if (status !== "in_call") return;
        const onKey = (e) => {
            const tag = (e.target && e.target.tagName) || "";
            if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
            if (/^[0-9*#]$/.test(e.key)) {
                e.preventDefault();
                sendDigit(e.key);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [status, sendDigit]);

    if (!open) return null;

    const overCap = usedToday >= dailyCap;

    return (
        <aside
            data-testid="dialer-panel"
            className="fixed right-0 z-30 w-[360px] border-l border-mm-line bg-white shadow-2xl"
            style={{ top: 56, bottom: 0 }}
        >
            <header className="flex items-center justify-between border-b border-mm-line bg-mm-bg/80 px-4 py-3">
                <div>
                    <div className="mm-eyebrow text-[10px]">Dialer</div>
                    <div className="text-sm font-semibold text-mm-navy">
                        {identity ? identity.replace("agent_", "") : "Loading…"}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-mm-muted">
                        {usedToday}/{dailyCap} today
                    </span>
                    <button
                        onClick={closePanel}
                        disabled={status === "in_call" || status === "ringing"}
                        className="rounded-full p-1.5 text-mm-muted hover:bg-mm-line/50 disabled:opacity-30"
                        data-testid="dialer-close"
                    >
                        <X size={14} />
                    </button>
                </div>
            </header>

            {error && (
                <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-700" data-testid="dialer-error">
                    <AlertCircle size={12} className="mr-1 inline" />
                    {error}
                </div>
            )}

            {/* Body */}
            <div className="h-[calc(100%-56px)] overflow-y-auto">
                {status === "incoming" && (
                    <IncomingState
                        from={currentTo}
                        onAccept={acceptIncoming}
                        onReject={rejectIncoming}
                    />
                )}
                {status === "ringing" && (
                    <RingingState target={currentTo} lead={currentLead} onCancel={hangup} />
                )}
                {status === "in_call" && (
                    <InCallState
                        target={currentTo}
                        lead={currentLead}
                        callTimer={callTimer}
                        muted={muted}
                        notes={notes}
                        savingNotes={savingNotes}
                        onMute={toggleMute}
                        onHangup={hangup}
                        onSendDigit={sendDigit}
                        onNotes={setNotes}
                    />
                )}
                {status === "post_call" && (
                    <PostCallState
                        lastCall={lastCall}
                        onComplete={completePostCall}
                    />
                )}
                {status === "idle" && (
                    <IdleState
                        currentTo={currentTo}
                        onChangeTo={setCurrentTo}
                        onPlace={() => placeCall({ to: currentTo })}
                        onSendDigit={sendDigit}
                        recentCalls={recentCalls}
                        onRedial={(n) => { setCurrentTo(n); placeCall({ to: n }); }}
                        overCap={overCap}
                        usedToday={usedToday}
                        dailyCap={dailyCap}
                    />
                )}
                {status === "initializing" && (
                    <div className="flex flex-col items-center justify-center py-16 text-mm-muted">
                        <Loader2 className="mb-3 animate-spin text-mm-navy" />
                        <div className="text-sm">Connecting to Twilio…</div>
                        <div className="mt-1 text-xs">First-time grant: allow microphone.</div>
                    </div>
                )}
            </div>
        </aside>
    );
}

// ----------------------- States -----------------------

function IdleState({
    currentTo, onChangeTo, onPlace, onSendDigit, recentCalls,
    onRedial, overCap, usedToday, dailyCap,
}) {
    const inputRef = useRef(null);

    // Auto-focus the tel input when entering idle so the user can immediately type.
    useEffect(() => {
        const t = setTimeout(() => {
            try {
                inputRef.current?.focus({ preventScroll: true });
                const el = inputRef.current;
                if (el) {
                    const len = el.value.length;
                    el.setSelectionRange(len, len);
                }
            } catch { /* ignore */ }
        }, 50);
        return () => clearTimeout(t);
    }, []);

    const handleChange = (e) => onChangeTo(sanitizeDialString(e.target.value));

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (currentTo) onPlace();
        } else if (e.key === "Escape") {
            // Esc clears the input — does NOT close the panel when there's content.
            if (currentTo) {
                e.preventDefault();
                e.stopPropagation();
                onChangeTo("");
            }
        }
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData("text");
        if (pasted == null) return;
        e.preventDefault();
        const cleaned = sanitizeDialString(pasted);
        const el = inputRef.current;
        const start = el?.selectionStart ?? currentTo.length;
        const end = el?.selectionEnd ?? currentTo.length;
        const next = sanitizeDialString(
            currentTo.slice(0, start) + cleaned + currentTo.slice(end),
        );
        onChangeTo(next);
        // Restore caret after the pasted block
        requestAnimationFrame(() => {
            try {
                const pos = Math.min(next.length, start + cleaned.length);
                el?.setSelectionRange(pos, pos);
            } catch { /* ignore */ }
        });
    };

    const previewE164 = currentTo ? normalizePhone(currentTo) : "";
    const isE164 = currentTo.startsWith("+") && currentTo.length >= 8;

    return (
        <div className="space-y-4 p-4" data-testid="dialer-state-idle">
            <div className="rounded-2xl border border-mm-line bg-white p-3">
                <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">To</div>
                    {currentTo && (
                        <button
                            type="button"
                            onClick={() => { onChangeTo(""); inputRef.current?.focus(); }}
                            aria-label="Clear number"
                            className="rounded-full p-1 text-mm-muted hover:bg-mm-bg hover:text-mm-ink"
                            data-testid="dialer-input-clear"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
                <input
                    ref={inputRef}
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={currentTo}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder="+1 555 555 0100"
                    className="mt-1 w-full bg-transparent text-2xl font-mono text-mm-ink outline-none placeholder:text-mm-line"
                    data-testid="dialer-input-to"
                />
                <FormatHint
                    raw={currentTo}
                    preview={previewE164}
                    isE164={isE164}
                />
            </div>

            <div className="grid grid-cols-3 gap-2">
                {KEYPAD.map(([n, sub]) => (
                    <button
                        key={n}
                        type="button"
                        tabIndex={-1}
                        onClick={() => onSendDigit(n)}
                        data-testid={`dialer-key-${n}`}
                        className="flex h-14 flex-col items-center justify-center rounded-xl border border-mm-line bg-mm-bg/40 transition-colors hover:bg-mm-bg active:scale-95"
                    >
                        <div className="text-xl font-medium text-mm-navy">{n}</div>
                        {sub && <div className="text-[9px] uppercase tracking-wider text-mm-muted">{sub}</div>}
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => onChangeTo(currentTo.slice(0, -1))}
                    className="rounded-xl border border-mm-line p-3 hover:bg-mm-bg"
                    aria-label="Backspace"
                    data-testid="dialer-backspace"
                >
                    <Delete size={16} className="text-mm-muted" />
                </button>
                <button
                    type="button"
                    onClick={onPlace}
                    disabled={!currentTo || overCap}
                    className="flex-1 rounded-xl bg-emerald-600 py-3 font-medium text-white shadow transition-colors hover:bg-emerald-700 disabled:opacity-40"
                    data-testid="dialer-call-button"
                >
                    <Phone size={14} className="mr-1.5 inline" /> Call
                </button>
            </div>

            {overCap && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    Daily call cap reached ({usedToday}/{dailyCap}). Contact admin to lift.
                </div>
            )}

            {recentCalls.length > 0 && (
                <div data-testid="dialer-recent-calls">
                    <div className="mb-2 mt-4 text-[10px] uppercase tracking-wider text-mm-muted">
                        Recent
                    </div>
                    <ul className="space-y-1.5">
                        {recentCalls.slice(0, 6).map((c) => (
                            <li
                                key={c.id}
                                onClick={() => onRedial(c.to_number || c.from_number || "")}
                                className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 text-xs hover:bg-mm-bg"
                            >
                                <div>
                                    <div className="font-medium text-mm-ink">
                                        {c.lead_name || c.to_number || c.from_number || "—"}
                                    </div>
                                    <div className="text-[10px] text-mm-muted">
                                        {c.direction === "inbound" ? "in" : "out"} · {c.status}
                                        {c.duration_seconds ? ` · ${c.duration_seconds}s` : ""}
                                    </div>
                                </div>
                                <Phone size={12} className="text-mm-muted" />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function FormatHint({ raw, preview, isE164 }) {
    if (!raw) {
        return (
            <div className="mt-1 text-[10px] text-mm-muted" data-testid="dialer-format-hint">
                Enter a number
            </div>
        );
    }
    if (isE164) {
        return (
            <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700" data-testid="dialer-format-hint">
                <CheckCircle2 size={10} />
                E.164 ready
            </div>
        );
    }
    if (preview && preview !== raw && preview.startsWith("+")) {
        return (
            <div className="mt-1 text-[10px] text-mm-navy" data-testid="dialer-format-hint">
                → <span className="font-mono">{preview}</span>
            </div>
        );
    }
    return (
        <div className="mt-1 text-[10px] text-amber-700" data-testid="dialer-format-hint">
            Add country code (e.g. +1 for US)
        </div>
    );
}

function RingingState({ target, lead, onCancel }) {
    return (
        <div className="flex h-full flex-col items-center justify-center p-6 text-center" data-testid="dialer-state-ringing">
            <div className="relative">
                <div className="absolute inset-0 animate-ping rounded-full bg-amber-400/40" />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-amber-500 text-white">
                    <PhoneCall size={32} />
                </div>
            </div>
            <div className="mt-6 text-lg font-semibold text-mm-navy">
                Calling {lead?.name || "—"}
            </div>
            <div className="mt-1 font-mono text-sm text-mm-muted">{target}</div>
            <button
                onClick={onCancel}
                className="mt-8 rounded-xl bg-rose-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-rose-700"
                data-testid="dialer-cancel-ringing"
            >
                <PhoneOff size={14} className="mr-1.5 inline" /> Cancel
            </button>
        </div>
    );
}

function IncomingState({ from, onAccept, onReject }) {
    return (
        <div className="flex h-full flex-col items-center justify-center p-6 text-center" data-testid="dialer-state-incoming">
            <div className="relative">
                <div className="absolute inset-0 animate-ping rounded-full bg-emerald-400/40" />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <PhoneIncoming size={32} />
                </div>
            </div>
            <div className="mt-6 text-lg font-semibold text-mm-navy">Incoming call</div>
            <div className="mt-1 font-mono text-sm text-mm-muted">{from}</div>
            <div className="mt-8 flex gap-3">
                <button
                    onClick={onReject}
                    className="rounded-xl bg-rose-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-rose-700"
                    data-testid="dialer-reject-incoming"
                >
                    <PhoneOff size={14} className="mr-1.5 inline" /> Reject
                </button>
                <button
                    onClick={onAccept}
                    className="rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"
                    data-testid="dialer-accept-incoming"
                >
                    <PhoneCall size={14} className="mr-1.5 inline" /> Answer
                </button>
            </div>
        </div>
    );
}

function InCallState({
    target, lead, callTimer, muted, notes, savingNotes,
    onMute, onHangup, onSendDigit, onNotes,
}) {
    const m = Math.floor(callTimer / 60);
    const s = String(callTimer % 60).padStart(2, "0");
    return (
        <div className="space-y-4 p-4" data-testid="dialer-state-in-call">
            <div className="text-center">
                <div className="text-3xl font-mono tabular-nums text-mm-navy">
                    {m}:{s}
                </div>
                <div className="mt-1 text-sm text-mm-muted">
                    {lead?.name || "Live call"} · <span className="font-mono">{target}</span>
                </div>
                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-600" />
                    Recording
                </div>
            </div>

            <div className="rounded-xl border border-mm-line bg-mm-bg/40 p-2">
                <div className="text-[10px] uppercase tracking-wider text-mm-muted">DTMF</div>
                <div className="mt-1 grid grid-cols-3 gap-1">
                    {KEYPAD.map(([n]) => (
                        <button
                            key={n}
                            onClick={() => onSendDigit(n)}
                            className="rounded-md bg-white py-1.5 text-sm font-medium text-mm-navy hover:bg-mm-line/40"
                            data-testid={`dialer-dtmf-${n}`}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">Quick notes</div>
                    {savingNotes && <div className="text-[10px] text-mm-muted">saving…</div>}
                </div>
                <Textarea
                    value={notes}
                    onChange={(e) => onNotes(e.target.value)}
                    placeholder="Anything they want, anything they object to, follow-up date…"
                    rows={4}
                    data-testid="dialer-quick-notes"
                />
                <div className="mt-1 flex items-center gap-1 text-[10px] text-mm-muted">
                    <ShieldCheck size={10} /> Auto-saves to call record
                </div>
            </div>

            <div className="flex gap-2">
                <button
                    onClick={onMute}
                    className={[
                        "flex-1 rounded-xl border py-2.5 text-sm font-medium transition-colors",
                        muted
                            ? "border-rose-200 bg-rose-50 text-rose-700"
                            : "border-mm-line bg-white text-mm-ink hover:bg-mm-bg",
                    ].join(" ")}
                    data-testid="dialer-mute"
                >
                    {muted ? <><MicOff size={14} className="mr-1.5 inline" /> Muted</> : <><Mic size={14} className="mr-1.5 inline" /> Mute</>}
                </button>
                <button
                    onClick={onHangup}
                    className="flex-1 rounded-xl bg-rose-600 py-2.5 text-sm font-medium text-white hover:bg-rose-700"
                    data-testid="dialer-hangup"
                >
                    <PhoneOff size={14} className="mr-1.5 inline" /> Hang up
                </button>
            </div>
        </div>
    );
}

// -------------------- Phase 5 — Post-call Call-Log form --------------------

const OUTCOMES = [
    { value: "answered_interested", label: "Answered · Interested" },
    { value: "callback_requested", label: "Callback Requested" },
    { value: "voicemail_left", label: "Voicemail Left" },
    { value: "no_answer", label: "No Answer" },
    { value: "policy_closed", label: "Policy Closed" },
    { value: "not_interested", label: "Not Interested" },
    { value: "wrong_number", label: "Wrong Number" },
];

const DRAFT_KEY = (callId) => `mm_dialer_draft_v1:${callId || "no_call"}`;
const fmtMoney = (c) =>
    c == null || c === "" ? "—" : `$${(Number(c) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PostCallState({ lastCall, onComplete }) {
    const callId = lastCall?.call_id;
    const [outcome, setOutcome] = useState("");
    const [notes, setNotes] = useState(lastCall?.prefill_notes || "");
    const [showTimes, setShowTimes] = useState(false);
    const [startAt, setStartAt] = useState(lastCall?.started_at || "");
    const [endAt, setEndAt] = useState(lastCall?.ended_at || "");
    const [carriers, setCarriers] = useState([]);
    const [policyTypes, setPolicyTypes] = useState([]);
    const [carrier, setCarrier] = useState("");
    const [policyType, setPolicyType] = useState("");
    const [coverage, setCoverage] = useState(""); // dollars
    const [monthly, setMonthly] = useState("");   // dollars
    const [policyNumber, setPolicyNumber] = useState("");
    const [effectiveDate, setEffectiveDate] = useState("");
    const [policyNotes, setPolicyNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // Restore draft + load catalogs
    useEffect(() => {
        if (!callId) return;
        try {
            const raw = localStorage.getItem(DRAFT_KEY(callId));
            if (raw) {
                const d = JSON.parse(raw);
                setOutcome(d.outcome || "");
                setNotes(d.notes || lastCall?.prefill_notes || "");
                setCarrier(d.carrier || "");
                setPolicyType(d.policy_type || "");
                setCoverage(d.coverage || "");
                setMonthly(d.monthly || "");
                setPolicyNumber(d.policy_number || "");
                setEffectiveDate(d.effective_date || "");
                setPolicyNotes(d.policy_notes || "");
            }
        } catch { /* ignore */ }
        Promise.all([
            adminApi.carriers({ active: true }).catch(() => []),
            adminApi.policyTypes({ active: true }).catch(() => []),
        ]).then(([c, t]) => { setCarriers(c); setPolicyTypes(t); });
         
    }, [callId]);

    // Persist draft on any field change
    useEffect(() => {
        if (!callId) return;
        try {
            localStorage.setItem(DRAFT_KEY(callId), JSON.stringify({
                outcome, notes, carrier, policy_type: policyType,
                coverage, monthly, policy_number: policyNumber,
                effective_date: effectiveDate, policy_notes: policyNotes,
            }));
        } catch { /* ignore */ }
    }, [callId, outcome, notes, carrier, policyType, coverage, monthly, policyNumber, effectiveDate, policyNotes]);

    const isPolicy = outcome === "policy_closed";
    const durationSec = lastCall?.duration_seconds || 0;
    const showSkip = durationSec < 10;

    const annualPreview = monthly && !isNaN(+monthly) ? +monthly * 12 : 0;

    const validate = () => {
        const e = {};
        if (!outcome) e.outcome = "Required";
        if (isPolicy) {
            if (!carrier) e.carrier = "Required";
            if (!policyType) e.policy_type = "Required";
            if (!coverage || +coverage <= 0) e.coverage = "Required";
            if (!monthly || +monthly <= 0) e.monthly = "Required";
            if (!effectiveDate) e.effective_date = "Required";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        if (!callId) {
            toast.error("No call found to log against.");
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                call_id: callId,
                outcome,
                notes,
                ...(showTimes && startAt ? { call_start_at: startAt } : {}),
                ...(showTimes && endAt ? { call_end_at: endAt } : {}),
            };
            if (isPolicy) {
                body.policy = {
                    carrier,
                    policy_type: policyType,
                    coverage_amount: Math.round(Number(coverage) * 100),
                    monthly_premium: Math.round(Number(monthly) * 100),
                    policy_number: policyNumber || undefined,
                    effective_date: effectiveDate,
                    notes: policyNotes || undefined,
                };
            }
            await adminApi.createCallLog(body);
            toast.success(isPolicy ? "Deal closed — logged." : "Call logged.");
            try { localStorage.removeItem(DRAFT_KEY(callId)); } catch { /* ignore */ }
            onComplete?.();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't save.";
            toast.error(Array.isArray(msg) ? msg[0]?.msg || "Validation error" : msg);
        } finally {
            setSubmitting(false);
        }
    };

    const skip = async () => {
        if (!callId) { onComplete?.(); return; }
        setSubmitting(true);
        try {
            await adminApi.createCallLog({
                call_id: callId,
                outcome: "no_answer",
                notes: "Auto-skipped (call <10s).",
            });
            try { localStorage.removeItem(DRAFT_KEY(callId)); } catch { /* ignore */ }
            toast("Skipped — logged as No Answer.");
            onComplete?.();
        } catch {
            toast.error("Couldn't skip cleanly.");
        } finally {
            setSubmitting(false);
        }
    };

    const m = Math.floor(durationSec / 60);
    const s = String(durationSec % 60).padStart(2, "0");

    return (
        <div className="space-y-3 p-4" data-testid="dialer-state-post-call">
            <div className="rounded-xl border border-mm-line bg-mm-bg/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-mm-muted">Call ended</div>
                <div className="mt-0.5 text-sm font-semibold text-mm-navy">
                    {lastCall?.lead?.name || "—"}
                    <span className="ml-2 font-mono text-xs font-normal text-mm-muted">
                        {lastCall?.to || ""}
                    </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-mm-muted">
                    <span className="tabular-nums">{m}:{s}</span>
                    <span>·</span>
                    <span>{lastCall?.direction || "outbound"}</span>
                    <button
                        type="button"
                        onClick={() => setShowTimes((x) => !x)}
                        className="ml-auto text-[10px] text-mm-navy underline-offset-2 hover:underline"
                        data-testid="post-call-edit-times"
                    >
                        {showTimes ? "Hide times" : "Edit times"}
                    </button>
                </div>
                {showTimes && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                        <label className="space-y-0.5">
                            <span className="text-mm-muted">Start</span>
                            <input
                                value={startAt} onChange={(e) => setStartAt(e.target.value)}
                                className="w-full rounded border border-mm-line bg-white px-1.5 py-1"
                                data-testid="post-call-start"
                            />
                        </label>
                        <label className="space-y-0.5">
                            <span className="text-mm-muted">End</span>
                            <input
                                value={endAt} onChange={(e) => setEndAt(e.target.value)}
                                className="w-full rounded border border-mm-line bg-white px-1.5 py-1"
                                data-testid="post-call-end"
                            />
                        </label>
                    </div>
                )}
            </div>

            <div>
                <label className="text-[10px] uppercase tracking-wider text-mm-muted">
                    Outcome <span className="text-rose-600">*</span>
                </label>
                <select
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    className={`mt-1 w-full rounded-lg border bg-white px-2 py-2 text-sm ${errors.outcome ? "border-rose-400" : "border-mm-line"}`}
                    data-testid="post-call-outcome"
                >
                    <option value="">Choose outcome…</option>
                    {OUTCOMES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                {errors.outcome && <div className="mt-0.5 text-[10px] text-rose-700">{errors.outcome}</div>}
            </div>

            <div>
                <label className="text-[10px] uppercase tracking-wider text-mm-muted">Notes</label>
                <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything relevant, follow-up details, objections…"
                    rows={3}
                    data-testid="post-call-notes"
                />
            </div>

            {isPolicy && (
                <div
                    className="space-y-2 rounded-xl border p-3"
                    style={{ backgroundColor: "#C9A84C0D", borderColor: "#C9A84C88" }}
                    data-testid="post-call-policy-section"
                >
                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#8A6B1E" }}>
                        💰 Policy details
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Carrier <span className="text-rose-600">*</span></span>
                            <select
                                value={carrier}
                                onChange={(e) => setCarrier(e.target.value)}
                                className={`w-full rounded border bg-white px-1.5 py-1.5 ${errors.carrier ? "border-rose-400" : "border-mm-line"}`}
                                data-testid="post-call-carrier"
                            >
                                <option value="">Choose…</option>
                                {carriers.map((c) => (
                                    <option key={c.id} value={c.name}>{c.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Type <span className="text-rose-600">*</span></span>
                            <select
                                value={policyType}
                                onChange={(e) => setPolicyType(e.target.value)}
                                className={`w-full rounded border bg-white px-1.5 py-1.5 ${errors.policy_type ? "border-rose-400" : "border-mm-line"}`}
                                data-testid="post-call-policy-type"
                            >
                                <option value="">Choose…</option>
                                {policyTypes.map((t) => (
                                    <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Coverage ($) <span className="text-rose-600">*</span></span>
                            <input
                                type="number" min="0" step="1" value={coverage}
                                onChange={(e) => setCoverage(e.target.value)}
                                placeholder="500000"
                                className={`w-full rounded border bg-white px-1.5 py-1.5 ${errors.coverage ? "border-rose-400" : "border-mm-line"}`}
                                data-testid="post-call-coverage"
                            />
                        </label>
                        <label className="space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Monthly ($) <span className="text-rose-600">*</span></span>
                            <input
                                type="number" min="0" step="0.01" value={monthly}
                                onChange={(e) => setMonthly(e.target.value)}
                                placeholder="45.00"
                                className={`w-full rounded border bg-white px-1.5 py-1.5 ${errors.monthly ? "border-rose-400" : "border-mm-line"}`}
                                data-testid="post-call-monthly"
                            />
                        </label>
                        <div className="col-span-2 rounded bg-white px-2 py-1 text-[11px] text-mm-muted">
                            Annual premium: <span className="font-semibold tabular-nums text-mm-navy">{fmtMoney(Math.round(annualPreview * 100))}</span>
                        </div>
                        <label className="space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Effective date <span className="text-rose-600">*</span></span>
                            <input
                                type="date" value={effectiveDate}
                                onChange={(e) => setEffectiveDate(e.target.value)}
                                className={`w-full rounded border bg-white px-1.5 py-1.5 ${errors.effective_date ? "border-rose-400" : "border-mm-line"}`}
                                data-testid="post-call-effective"
                            />
                        </label>
                        <label className="space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Policy #</span>
                            <input
                                value={policyNumber}
                                onChange={(e) => setPolicyNumber(e.target.value)}
                                placeholder="optional"
                                className="w-full rounded border border-mm-line bg-white px-1.5 py-1.5"
                                data-testid="post-call-policy-number"
                            />
                        </label>
                        <label className="col-span-2 space-y-0.5 text-[11px]">
                            <span className="text-mm-muted">Close notes</span>
                            <textarea
                                value={policyNotes}
                                onChange={(e) => setPolicyNotes(e.target.value)}
                                rows={2}
                                className="w-full rounded border border-mm-line bg-white px-1.5 py-1.5"
                                data-testid="post-call-policy-notes"
                            />
                        </label>
                    </div>
                </div>
            )}

            <div className="flex gap-2 pt-1">
                {showSkip && (
                    <button
                        type="button"
                        onClick={skip}
                        disabled={submitting}
                        className="rounded-xl border border-mm-line bg-white px-4 py-2.5 text-xs font-medium text-mm-muted hover:bg-mm-bg"
                        data-testid="post-call-skip"
                    >
                        Skip
                    </button>
                )}
                <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-medium text-white shadow hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="post-call-submit"
                >
                    {submitting ? "Saving…" : "Submit & Continue"}
                </button>
            </div>
        </div>
    );
}

